import { faAnglesDown, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { NewTagGq } from '../../../graphql/projects/mutations/tags/useAddTagsGq';
import AddTagsDialog from '../TagSection/Dialogs/AddTagsDialog';

export interface AddTagsButtonProps {
  tagGroup: TagGroupGq;
  reload: () => Promise<unknown>;
}

export const AddTagsButton: React.FC<AddTagsButtonProps> = ({ tagGroup, reload }) => {
  const [openAdvancedDialog, setOpenAdvancedDialog] = useState(false);

  // Popover
  const [open, setOpen] = useState(false);
  const [inputRef, setInputRef] = useState<HTMLTextAreaElement>(null);

  // Clean up logic
  const cleanUp = () => {
    setTextValue('');
    setTags([]);
  };

  // Clean up when changing tags
  useEffect(() => {
    cleanUp();
  }, [tagGroup.id]);

  // Logic for handling text value manipulation
  const [textValue, setTextValue] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    const lastSeparatorPosition = Array.from(textValue.matchAll(/[\n,]+/gm)).reverse()[0]?.index;
    if (!!lastSeparatorPosition || lastSeparatorPosition == 0) {
      const newTags = textValue
        .slice(0, lastSeparatorPosition)
        .split(/[\n,]+/)
        .map((tv) => tv.trim().toLowerCase())
        .filter((v) => !!v.length)
        .map((tv) => tv.slice(0, 1).toUpperCase() + tv.slice(1, tv.length));

      setTags((prev) => [...prev, ...newTags]);
      setTextValue(textValue.slice(lastSeparatorPosition + 1, textValue.length));
    }
  }, [textValue]);

  const backspaceHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && textValue.length === 0 && !!tags.length) {
      setTags((prev) => prev.slice(0, prev.length - 1));
    }
  };

  // Adding tags
  const [submitting, setSubmitting] = useState(false);
  const addMutation = projectsApiService.mutations.tags.useAddTags();
  const handleAdd = () => {
    setSubmitting(true);
    const newTags = [...tags.map((t): NewTagGq => ({ name: t, textMatchVariants: t }))];
    const cleanedTextValue = textValue.trim().toLowerCase();
    if (cleanedTextValue.length) {
      newTags.push({ name: cleanedTextValue, textMatchVariants: cleanedTextValue });
    }
    addMutation({ projectId: tagGroup.projectId, tagGroupId: tagGroup.id, tags: newTags })
      .then(reload)
      .finally(() => {
        setSubmitting(false);
        setOpen(false);
        cleanUp();
      });
  };

  const [focused, setFocused] = useState(false);

  return (
    <>
      {openAdvancedDialog && (
        <AddTagsDialog
          openDialog={openAdvancedDialog}
          setOpenDialog={(open) => {
            setOpenAdvancedDialog(open);
            setOpen(true);
          }}
          tagGroup={tagGroup}
          onSuccess={() => {
            cleanUp();
            return reload().finally(() => {
              setOpen(false);
              setOpenAdvancedDialog(false);
            });
          }}
        />
      )}
      <VoxelPopover open={open} onOpenChange={setOpen}>
        <VoxelPopoverTrigger enableActive>
          <VoxelButton
            title="Add tags"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            size="sm"
            color={!!tagGroup.tags.length ? 'gray' : 'success'}
          />
        </VoxelPopoverTrigger>
        <VoxelPopoverContent>
          <div className="w-96 p-6 flex flex-col gap-6">
            <div className="flex flex-row items-center justify-end absolute top-2 right-2">
              <VoxelIconButton
                color="white"
                icon={<FontAwesomeIcon icon={faTimes} />}
                onClick={() => setOpen(false)}
              />
            </div>
            <div className="flex flex-col gap-2.5">
              <VoxelText variant="form-caption">Tags</VoxelText>
              <div
                className={`
                w-full pb-12 rounded
                px-3 pt-3
                flex flex-row flex-wrap items-center gap-2.5
                ${!focused && 'ring-1 ring-voxel-gray-200'}
                ${focused && 'ring-2 ring-primary-600'}
                focus:ring-2 focus:ring-primary-600
                active:ring-2 active:ring-primary-600
                `}
                onClick={() => inputRef?.focus()}
              >
                {tags.map((tag, tagIndex) => (
                  <div
                    key={tagIndex}
                    className="bg-voxel-blue-50/80 pl-2 pr-0.5 py-1 rounded-md text-voxel-blue-600 flex flex-row items-center gap-0.5"
                  >
                    {tag}
                    <button
                      className="rounded-md hover:bg-black/10  w-[17px] h-[17px] flex items-center justify-center opacity-30 hover:opacity-60 cursor-pointer"
                      onClick={() =>
                        setTags((prev) => prev.filter((p, index) => index !== tagIndex))
                      }
                    >
                      <FontAwesomeIcon icon={faTimes} fontSize={10} />
                    </button>
                  </div>
                ))}
                <textarea
                  value={textValue}
                  ref={setInputRef}
                  onKeyDown={backspaceHandler}
                  onChange={(event) => setTextValue(event.target.value)}
                  className="outline-none flex-1 min-w-[100px] h-7 resize-none py-1"
                  rows={1}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  autoFocus
                  placeholder="Tag name ..."
                  disabled={submitting}
                />
              </div>
              <VoxelText variant="form-caption">
                Separate multiple tags with comma or enter
              </VoxelText>
            </div>

            <div className="flex flex-row items-center justify-end gap-2">
              <VoxelButton
                title="Advanced"
                color="white"
                endIcon={<FontAwesomeIcon icon={faAnglesDown} />}
                size="sm"
                disabled={submitting}
                onClick={() => {
                  setOpenAdvancedDialog(true);
                  setOpen(false);
                }}
              />
              <VoxelButton
                title="Confirm"
                color="primary"
                onClick={() => handleAdd()}
                submitting={submitting}
                disabled={submitting}
              />
            </div>
          </div>
        </VoxelPopoverContent>
      </VoxelPopover>
    </>
  );
};

export default AddTagsButton;
